import { type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import { Box, Menu, MenuItem } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Grid } from '@mui/system';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { CapTableDetailsModel, SpreadsheetNote } from 'api';
import type { WaterfallView } from 'api/models/WaterfallView';
import { captableUrl, fundOwnershipUrl } from 'common/config/urls';
import { WATERFALL_SLUG } from 'common/constants/allocations';
import { CAP_TABLE_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import { CAP_TABLE_REFERENCE_TITLE, CAP_TABLE_REFERENCE_TYPE } from 'common/constants/documents';
import { WATERFALL_PAGE_KEY } from 'common/constants/notes';
import { useStore } from 'common/store';
import { CapTableVersionWithCompanyId, CompanyInfo } from 'common/types/company';
import { UseStoreValues } from 'common/types/store';
import { CustomCompactContainer, CustomContainer } from 'components';
import CurrencyMenu from 'components/CurrencyMenu';
import ConfirmationDelete from 'components/Dialogs/ConfirmationDelete';
import ConfirmationDialog from 'components/Dialogs/ConfirmationDialog';
import WidgetDrawer from 'components/Drawer/widget-drawer/WidgetDrawer';
import { WidgetDrawerApplicationContext } from 'components/Drawer/widget-drawer/WidgetDrawer.props';
import EmptyTableMessage from 'components/EmptyTableMessage';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import type { ScalarSpreadsheetProps } from 'components/ScalarSpreadsheet/ScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import { Alert, Button, OverflowTooltip, TextLink } from 'components/ui';
import {
  useSecondaryNavigationContributionRegistration,
  useTertiaryNavigationRegistration,
} from 'context/FirmBaseLayoutContext';
import type {
  TertiaryNavigationActionControlConfig,
  TertiaryNavigationLeftActionControlConfig,
} from 'context/FirmBaseLayoutContext';
import { NeedsFundOwnershipFirst } from 'pages/Allocation/allocations/components';
import { getValidSecurities, useGenerateChildrenCompaniesCapTablesVersions } from 'pages/CapTable/cap-table/utilities';
import {
  ALLOCATION_EMPTY_FUND_OWNERSHIP_TAGLINE,
  ALLOCATION_EMPTY_FUND_OWNERSHIP_TITLE,
} from 'pages/ValuationsAllocation/common/constants/allocation/allocation';
import {
  DownloadWaterfall,
  WaterfallLoadingSkeleton,
  WaterfallTableFormatter,
  WaterfallTables,
} from 'pages/Waterfall/components';
import { getFirmWaterfallCompanies } from 'pages/Waterfall/components/CompanyList/CompanyList.utils';
import { useWaterfallSkeletonFundInvestmentSheetCount } from 'pages/Waterfall/components/WaterfallLoadingSkeleton/useWaterfallSkeletonFundInvestmentSheetCount';
import WaterfallRecalculation from 'pages/Waterfall/components/WaterfallRecalculation';
import WaterfallContext from 'pages/Waterfall/data/WaterfallContext/WaterfallContext';
import { CAP_TABLE_ALIAS, MEASUREMENT_DATE_ALIAS } from 'pages/Waterfall/SpreadsheetConfig/constants';
import useFundInvestmentSheetConfig from 'pages/Waterfall/SpreadsheetConfig/FundInvestmentSheetConfig/useFundInvestmentSheetConfig/useFundInvestmentSheetConfig';
import { useWaterfallFundSummarySheetConfig } from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/useFundSummarySheetConfig/useWaterfallFundSummarySheetConfig';
import useCreateInputSheet from 'pages/Waterfall/SpreadsheetConfig/InputSheetConfig/useCreateInputSheet/useCreateInputSheet';
import { createWaterfallInputUnitPolicy } from 'pages/Waterfall/SpreadsheetConfig/InputSheetConfig/waterfallInputUnitPolicy';
import { useExchangeRateSheet } from 'pages/Waterfall/SpreadsheetConfig/WaterfallFX/ExchangeRateSheet/useExchangeRateSheet';
import { useWaterfallFXSync } from 'pages/Waterfall/SpreadsheetConfig/WaterfallFX/useWaterfallFXSync/useWaterfallFXSync';
import { WaterfallFXContext } from 'pages/Waterfall/SpreadsheetConfig/WaterfallFX/WaterfallFXContext/WaterfallFXContext';
import useWaterfallSheetConfig from 'pages/Waterfall/SpreadsheetConfig/WaterfallSheetConfig/useWaterfallSheetConfig/useWaterfallSheetConfig';
import { WaterfallDisplayProps } from 'pages/Waterfall/WaterfallDisplay/WaterfallDisplay.props';
import {
  getActiveRelatedEntitySheets,
  shouldFetchRelatedEntityCapTableDetail,
  shouldReleaseFullWaterfallSkeleton,
} from 'pages/Waterfall/WaterfallDisplay/WaterfallDisplay.utils';
import { useCompanyMeasurementDateFilter, useDocuments } from 'services/hooks';
import { useGetCompanyInfoById } from 'services/hooks/company';
import { useNotes } from 'services/hooks/notes';
import useSpreadsheetNotes from 'services/hooks/spreadsheetNotes/useSpreadsheetNotes';
import RelatedEntityWaterfallSheets from '../components/RelatedEntityWaterfallSheets/RelatedEntityWaterfallSheets';
import SaveWaterfallViewDialog from '../components/SaveWaterfallViewDialog';
import { WATERFALL_QUERY_PARAM_VIEW, WATERFALL_UNITS_TO_CURRENCY_FORMAT } from '../constants';
import {
  syncRelatedEntityInputAdjustments,
  syncRelatedEntityInputValidation,
  useRelatedEntityInputSheets,
} from '../hooks/useRelatedEntityInputSheets/useRelatedEntityInputSheets';
import { useWaterfallComputedValues } from '../hooks/useWaterfallComputedValues/useWaterfallComputedValues';
import { useWaterfallData } from '../hooks/useWaterfallData/useWaterfallData';
import { useWaterfallDistribution } from '../hooks/useWaterfallDistribution/useWaterfallDistribution';
import { useWaterfallInputHandlers } from '../hooks/useWaterfallInputHandlers/useWaterfallInputHandlers';
import { useWaterfallInvestedCapital } from '../hooks/useWaterfallInvestedCapital/useWaterfallInvestedCapital';
import { useWaterfallIrr } from '../hooks/useWaterfallIrr/useWaterfallIrr';
import { useWaterfallUrlDefaults } from '../hooks/useWaterfallUrlDefaults/useWaterfallUrlDefaults';
import { useWaterfallUrlState } from '../hooks/useWaterfallUrlState/useWaterfallUrlState';
import useWaterfallViewNavItems, { DEFAULT_VIEW_VALUE } from '../hooks/useWaterfallViewNavItems';
import {
  useCreateWaterfallView,
  useDeleteWaterfallView,
  useUpdateWaterfallView,
  useWaterfallViews,
} from '../hooks/useWaterfallViews';
import useFirmTotalSheetConfig from '../SpreadsheetConfig/FirmInvestmentSheetConfig/UseFirmTotalSheetConfig/useFirmTotalSheetConfig';
import { SecurityForWaterfall } from '../types';
import { useRelatedEntitiesWaterfalls } from '../utilities';
import {
  serializeRelatedEntityAdjustments,
  serializeSavedRelatedEntityAdjustments,
} from '../utilities/relatedEntityAdjustments';

const WATERFALL_CONTAINER_CLASS = `${WATERFALL_SLUG}-container`;
const WATERFALL_INPUT_CONTAINER_CLASS = `${WATERFALL_SLUG}-input-container`;
const WATERFALL_SUMMARY_CONTAINER_CLASS = `${WATERFALL_SLUG}-summary-container`;
const WATERFALL_RELATED_ENTITY_CONTAINER_CLASS = `${WATERFALL_SLUG}-related-entity-container`;
// Keep spreadsheets mounted for layout measurement without letting them shift the visible skeleton.
const HIDDEN_WATERFALL_PRESENTATION_STYLES = {
  pointerEvents: 'none',
  position: 'absolute',
  visibility: 'hidden',
  width: '100%',
} as const;
// Match the two-column Input table while allowing the notice to shrink on narrow viewports.
const UNAVAILABLE_COMPANY_NOTICE_STYLES = {
  boxSizing: 'border-box',
  mb: 1,
  ml: 1.5,
  mt: 1,
  maxWidth: 'calc(100% - 12px)',
  width: 472,
} as const;

type WaterfallViewDialogState = { mode: 'create' } | { initialName: string; mode: 'edit'; viewId: number };

const WaterfallDisplay = ({ isFirmPage }: WaterfallDisplayProps) => {
  // ============================================================
  // External hooks (documents, notes, store, etc.)
  // ============================================================
  const { filesToSave, setFilesToSave, addReferenceForExistingDocuments } = useDocuments();
  const { notes, setNotes, notesHasChanged, saveNotes, onAddNote, onUpdateNotes, onDeleteNote } = useNotes();
  const {
    spreadsheetNotes,
    setSpreadsheetNotes,
    saveSpreadsheetNotes,
    hasChanged: spreadsheetNoteHasChanged,
    handleOnDeleteNote: handleOnDeleteSpreadsheetNote,
  } = useSpreadsheetNotes();
  const getCompanyInfoById = useGetCompanyInfoById();
  const { selectedMeasurementDate: selectedMeasurementDateFromRoute, MDDialogue } = useCompanyMeasurementDateFilter();
  const [{ companyInfo, firmInfo, fundList, companyList }] = useStore() as UseStoreValues;
  const { id: companyIdFromRoute, child_companies: childCompaniesFromRoute } = companyInfo as CompanyInfo;
  const firmSlug = firmInfo?.slug ?? '';
  const companySlug = companyInfo?.slug ?? '';
  const navigate = useNavigate();
  const waterfallCompanyList = useMemo(
    () => (isFirmPage ? getFirmWaterfallCompanies(companyList) : companyList),
    [companyList, isFirmPage]
  );

  // ============================================================
  // URL State (Source of Truth)
  // ============================================================
  const urlState = useWaterfallUrlState({
    isFirmPage: isFirmPage ?? false,
    companyList: waterfallCompanyList,
    capTableList: undefined, // Will be set after data loads
    companyMeasurementDates: undefined, // Will be set after data loads
  });

  // Determine effective company ID (from URL for firm page, from route for company page)
  const effectiveCompanyId = isFirmPage ? urlState.companyIdFromUrl : companyIdFromRoute;

  // ============================================================
  // Data Fetching (depends on URL state)
  // ============================================================
  const waterfallData = useWaterfallData({
    companyId: effectiveCompanyId,
    measurementDateValue: urlState.measurementDateValueFromUrl,
    captableId: urlState.captableIdFromUrl,
    selectedMeasurementDateFromRoute,
    isFirmPage: isFirmPage ?? false,
  });

  // Re-create URL state with loaded data for proper resolution
  const urlStateWithData = useWaterfallUrlState({
    isFirmPage: isFirmPage ?? false,
    companyList: waterfallCompanyList,
    capTableList: waterfallData.capTableList,
    companyMeasurementDates: waterfallData.companyMeasurementDates,
  });
  const { clearView } = urlStateWithData;

  // ============================================================
  // Initialize URL Defaults
  // ============================================================
  const {
    isInitialized,
    isInitializing,
    isInitializationUnavailable,
    reset: resetUrlDefaults,
  } = useWaterfallUrlDefaults({
    urlState: urlStateWithData,
    isFirmPage: isFirmPage ?? false,
    mostRecentMeasurementDate: waterfallData.mostRecentMeasurementDate,
    primaryCaptable: waterfallData.primaryCaptable,
    companyMeasurementDates: waterfallData.companyMeasurementDates,
    capTableList: waterfallData.capTableList,
    isMeasurementDatesLoading: waterfallData.isMeasurementDatesLoading,
    isCapTableListLoading: waterfallData.isCapTableListLoading,
    hasMeasurementDatesSettled: waterfallData.hasMeasurementDatesSettled,
    hasCapTableListSettled: waterfallData.hasCapTableListSettled,
    companyIdFromRoute,
    selectedMeasurementDateFromRoute,
  });

  // ============================================================
  // Computed Values
  // ============================================================
  const computedValues = useWaterfallComputedValues({
    equityValue: urlStateWithData.equityValueFromUrl,
    enterpriseValue: urlStateWithData.enterpriseValueFromUrl,
    cashOverride: urlStateWithData.cashOverrideFromUrl,
    debtOverride: urlStateWithData.debtOverrideFromUrl,
    financials: waterfallData.financials,
  });

  // ============================================================
  // Derived values for compatibility with existing code
  // ============================================================
  const selectedCompanyId = effectiveCompanyId;
  // IMPORTANT: For firm pages, always use URL state as source of truth
  // For company pages, use resolvedMeasurementDate from route context
  const companySelectedMeasurementDate = isFirmPage
    ? urlStateWithData.selectedMeasurementDate
    : waterfallData.resolvedMeasurementDate || urlStateWithData.selectedMeasurementDate;
  const selectedCaptable = urlStateWithData.captableIdFromUrl;

  const exitDate = urlStateWithData.exitDateFromUrl ?? null;
  const equityValue = urlStateWithData.equityValueFromUrl ?? null;
  const enterpriseValue = urlStateWithData.enterpriseValueFromUrl ?? null;
  const { captableDetail, fundOwnershipCapTableDetail, isCaptableDetailLoading, refetchCaptableDetail } =
    waterfallData;
  const { capTableList } = waterfallData;
  const { companyMeasurementDates } = waterfallData;

  // Ownership bottoms (fund summary, FX) use the selected pro forma's own FO when it has
  // one, else fall back to primary FO — same fallback as Valuations.
  const ownershipCaptable = useMemo((): CapTableDetailsModel | null => {
    if (fundOwnershipCapTableDetail?.fund_ownership) {
      return fundOwnershipCapTableDetail as CapTableDetailsModel;
    }
    return captableDetail;
  }, [captableDetail, fundOwnershipCapTableDetail]);

  const { calculatedEquityValue, effectiveCash, effectiveDebt, cashIsFromFinancials, debtIsFromFinancials } =
    computedValues;
  const { ensureCurrencyDefaults, setForeignExchangeRate } = urlStateWithData;

  // Materialize defaults in the URL so shared links and saved views reproduce the same display.
  useEffect(() => {
    ensureCurrencyDefaults();
  }, [ensureCurrencyDefaults]);

  const handleResolvedExchangeRate = useCallback(
    (exchangeRate: number) => {
      setForeignExchangeRate(exchangeRate, false);
    },
    [setForeignExchangeRate]
  );

  const {
    format,
    inputFormat,
    formatDispatch,
    waterfallFXProvider,
    exchangeRateSheet,
    isAdditionalCurrencySelected,
    isCurrencyLoading,
    isExchangeRateReady,
    hasExchangeRateLoadError,
    isExchangeRateRetrying,
    refetchExchangeRate,
    currencyMenuAnchorEl,
    handleCurrencyMenuOpen,
    handleCurrencyMenuClose,
    handleCurrencyChange,
    handleUnitClick,
    unitDisplayLabel,
    currencyDisplayLabel,
    currencyTooltipTitle,
    currencies,
    usedCurrencies,
    showMore,
    showLess,
  } = useWaterfallFXSync({
    companyId: selectedCompanyId,
    page: CAP_TABLE_CURRENCY_PAGE,
    units: urlStateWithData.currencyUnitsFromUrl,
    measurementDate: companySelectedMeasurementDate,
    marketDate: exitDate,
    selectedCurrency: urlStateWithData.foreignCurrencyFromUrl,
    exchangeRateFromUrl: urlStateWithData.foreignExchangeRateFromUrl,
    isExchangeRateOverridden: urlStateWithData.isForeignExchangeRateOverriddenFromUrl,
    onCurrencyChange: urlStateWithData.setForeignCurrency,
    onUnitsChange: urlStateWithData.setCurrencyUnits,
    onExchangeRateResolved: handleResolvedExchangeRate,
  });

  const selectedResultCurrency = String(urlStateWithData.foreignCurrencyFromUrl);
  const {
    data: historicalInvestedCapital,
    isError: hasInvestedCapitalLoadError,
    isFetching: isInvestedCapitalFetching,
    isLoading: isInvestedCapitalLoading,
    refetch: refetchInvestedCapital,
  } = useWaterfallInvestedCapital({
    capTableId: selectedCaptable,
    enabled: isInitialized,
    targetCurrency: selectedResultCurrency,
  });

  const historicalInvestedCapitalTotals = useMemo(
    () => (historicalInvestedCapital?.currency === selectedResultCurrency ? historicalInvestedCapital.totals : null),
    [historicalInvestedCapital, selectedResultCurrency]
  );

  // ============================================================
  // Local State (UI-specific, not in URL)
  // ============================================================
  const [childrenCapTablesResult, setChildrenCapTablesResult] = useState<{
    requestKey: string;
    data: CapTableVersionWithCompanyId[];
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const childrenCapTablesDate = companySelectedMeasurementDate?.date;
  const childrenCapTablesRequestKey =
    selectedCompanyId && childrenCapTablesDate ? `${selectedCompanyId}-${childrenCapTablesDate}` : null;
  const childrenCapTables = useMemo(
    () =>
      childrenCapTablesRequestKey && childrenCapTablesResult?.requestKey === childrenCapTablesRequestKey
        ? childrenCapTablesResult.data
        : [],
    [childrenCapTablesRequestKey, childrenCapTablesResult]
  );
  const isChildrenCapTablesLoading =
    childrenCapTablesRequestKey != null && childrenCapTablesResult?.requestKey !== childrenCapTablesRequestKey;
  const { generateChildrenCompaniesCapTablesVersions } = useGenerateChildrenCompaniesCapTablesVersions(
    companyList,
    childrenCapTablesDate,
    selectedCompanyId ? Number(selectedCompanyId) : undefined
  );

  // Keep child Cap Tables tied to the active company/date and ignore responses from superseded requests.
  useEffect(() => {
    if (childrenCapTablesRequestKey == null) {
      setChildrenCapTablesResult(null);
      return undefined;
    }

    let isCurrentRequest = true;
    const fetchAndSetChildrenCapTables = async () => {
      const result = await generateChildrenCompaniesCapTablesVersions();
      const allChildrenCapTables = Object.values(result).flat();
      if (isCurrentRequest) {
        setChildrenCapTablesResult({
          requestKey: childrenCapTablesRequestKey,
          data: allChildrenCapTables.filter(ct => ct.is_primary),
        });
      }
    };
    fetchAndSetChildrenCapTables();
    return () => {
      isCurrentRequest = false;
    };
  }, [childrenCapTablesRequestKey, generateChildrenCompaniesCapTablesVersions]);

  // A related-entity selection can load only after its request-scoped cap table list settles.
  useEffect(() => {
    if (
      selectedCaptable != null &&
      shouldFetchRelatedEntityCapTableDetail({
        childCapTableIds: childrenCapTables
          .map(capTable => capTable.id)
          .filter((capTableId): capTableId is number => capTableId != null),
        isCaptableDetailLoading,
        isChildrenCapTablesLoading,
        loadedCaptableId: captableDetail?.id,
        selectedCaptableId: selectedCaptable,
      })
    ) {
      refetchCaptableDetail(selectedCaptable);
    }
  }, [
    captableDetail?.id,
    childrenCapTables,
    isCaptableDetailLoading,
    isChildrenCapTablesLoading,
    refetchCaptableDetail,
    selectedCaptable,
  ]);

  const fundInvestmentsSheetsNamesRef = useRef<string[]>([]);
  const mainFundOwnershipRef = useRef<CapTableDetailsModel['fund_ownership'] | null>(null);

  // ============================================================
  // Callbacks for saving
  // ============================================================
  const saveDrawer = useCallback(async () => {
    const saveTasks: Promise<unknown>[] = [];

    if (!isEmpty(filesToSave) && companySelectedMeasurementDate && selectedCaptable) {
      saveTasks.push(
        addReferenceForExistingDocuments(
          Number(companySelectedMeasurementDate.id),
          filesToSave,
          Number(selectedCaptable),
          CAP_TABLE_REFERENCE_TYPE
        )
      );
    }

    if (!isEmpty(notes) && notesHasChanged) {
      saveTasks.push(
        new Promise<void>((resolve, reject) => {
          saveNotes(undefined, {
            onSettled: resolve,
            onError: reject,
          });
        })
      );
    }

    if (!isEmpty(spreadsheetNotes) && spreadsheetNoteHasChanged) {
      saveTasks.push(saveSpreadsheetNotes());
    }

    if (saveTasks.length > 0) {
      await Promise.all(saveTasks);
    }
  }, [
    filesToSave,
    companySelectedMeasurementDate,
    selectedCaptable,
    notes,
    notesHasChanged,
    spreadsheetNotes,
    spreadsheetNoteHasChanged,
    addReferenceForExistingDocuments,
    saveNotes,
    saveSpreadsheetNotes,
  ]);

  // ============================================================
  // Saved Views
  // ============================================================
  const [viewDialogState, setViewDialogState] = useState<WaterfallViewDialogState | null>(null);

  const savedViewsCompanyId = isFirmPage ? (urlStateWithData.companyIdFromUrl ?? null) : (companyIdFromRoute ?? null);

  const savedViewsMeasurementDateId = urlStateWithData.selectedMeasurementDate?.cmd_id ?? null;
  const { data: savedViewsData, isFetching: isSavedViewsFetching } = useWaterfallViews(
    savedViewsCompanyId,
    savedViewsMeasurementDateId
  );
  const savedViews = useMemo(() => savedViewsData ?? [], [savedViewsData]);
  const { mutateAsync: createWaterfallView, isPending: isCreatingView } = useCreateWaterfallView();
  const { mutateAsync: deleteWaterfallView, isPending: isDeletingView } = useDeleteWaterfallView();
  const { mutateAsync: updateWaterfallView, isPending: isUpdatingView } = useUpdateWaterfallView();
  const [viewMenuState, setViewMenuState] = useState<{ anchorEl: HTMLButtonElement; viewId: number } | null>(null);
  const [deleteConfirmViewId, setDeleteConfirmViewId] = useState<number | null>(null);
  // Tracks which saved view the user explicitly navigated to; stays set even when URL params diverge
  // from the saved view's stored state, preventing the tab from auto-reverting to "Current".
  const [selectedViewId, setSelectedViewId] = useState<number | null>(null);
  const handledSavedViewContextRef = useRef<string | null>(null);
  const hydratedSavedViewKeyRef = useRef<string | null>(null);
  const isHydratingSavedViewRef = useRef(false);
  const hasExplicitInitialViewStateRef = useRef(
    Object.keys(urlStateWithData.parsedQuery).some(
      key => key !== 'company' && key !== 'date' && key !== WATERFALL_QUERY_PARAM_VIEW
    )
  );

  const handleSubmitView = useCallback(
    async (name: string) => {
      if (viewDialogState?.mode === 'edit') {
        await updateWaterfallView({ id: viewDialogState.viewId, payload: { name } });
        setViewDialogState(null);
        return;
      }

      if (
        viewDialogState?.mode !== 'create' ||
        savedViewsCompanyId == null ||
        savedViewsMeasurementDateId == null ||
        !isExchangeRateReady
      ) {
        return;
      }
      const newView = await createWaterfallView({
        company: savedViewsCompanyId,
        name,
        measurement_date: urlStateWithData.selectedMeasurementDate?.cmd_id ?? null,
        cap_table: urlStateWithData.captableIdFromUrl ?? null,
        exit_date: urlStateWithData.exitDateFromUrl
          ? moment(urlStateWithData.exitDateFromUrl, 'MM/DD/YYYY').format('YYYY-MM-DD')
          : null,
        exit_equity_value:
          urlStateWithData.equityValueFromUrl != null ? String(urlStateWithData.equityValueFromUrl) : null,
        exit_enterprise_value:
          urlStateWithData.enterpriseValueFromUrl != null ? String(urlStateWithData.enterpriseValueFromUrl) : null,
        plus_cash: urlStateWithData.cashOverrideFromUrl != null ? String(urlStateWithData.cashOverrideFromUrl) : null,
        less_debt: urlStateWithData.debtOverrideFromUrl != null ? String(urlStateWithData.debtOverrideFromUrl) : null,
        related_entity_adjustments: serializeSavedRelatedEntityAdjustments(
          urlStateWithData.relatedEntityAdjustmentsFromUrl
        ),
        foreign_currency: String(urlStateWithData.foreignCurrencyFromUrl),
        currency_format: WATERFALL_UNITS_TO_CURRENCY_FORMAT[
          urlStateWithData.currencyUnitsFromUrl
        ] as WaterfallView['currency_format'],
        foreign_exchange_rate:
          urlStateWithData.foreignExchangeRateFromUrl != null
            ? String(urlStateWithData.foreignExchangeRateFromUrl)
            : null,
        foreign_exchange_rate_is_overridden: urlStateWithData.isForeignExchangeRateOverriddenFromUrl,
        foreign_exchange_rate_input_currency:
          urlStateWithData.foreignExchangeRateInputCurrencyFromUrl as WaterfallView['foreign_exchange_rate_input_currency'],
      });
      setSelectedViewId(newView.id ?? null);
      setViewDialogState(null);
    },
    [
      createWaterfallView,
      isExchangeRateReady,
      savedViewsCompanyId,
      savedViewsMeasurementDateId,
      updateWaterfallView,
      urlStateWithData,
      viewDialogState,
    ]
  );

  const handleSelectCurrentView = useCallback(() => {
    resetUrlDefaults();
    setSelectedViewId(null);
  }, [resetUrlDefaults]);

  const handleOpenCreateViewDialog = useCallback(() => setViewDialogState({ mode: 'create' }), []);

  const handleCloseViewDialog = useCallback(() => setViewDialogState(null), []);

  const handleItemMenuClick = useCallback((event: MouseEvent<HTMLButtonElement>, item: { value: string }) => {
    const viewId = parseInt(item.value, 10);
    if (!Number.isNaN(viewId)) {
      setViewMenuState({ anchorEl: event.currentTarget, viewId });
    }
  }, []);

  const handleCloseViewMenu = useCallback(() => setViewMenuState(null), []);

  const handleRequestEditView = useCallback(() => {
    if (viewMenuState == null) {
      return;
    }
    const view = savedViews.find(savedView => savedView.id === viewMenuState.viewId);
    if (view?.id == null) {
      return;
    }
    setViewDialogState({ initialName: view.name, mode: 'edit', viewId: view.id });
    setViewMenuState(null);
  }, [savedViews, viewMenuState]);

  const handleRequestDeleteView = useCallback(() => {
    if (viewMenuState == null) {
      return;
    }
    setDeleteConfirmViewId(viewMenuState.viewId);
    setViewMenuState(null);
  }, [viewMenuState]);

  const handleConfirmDeleteView = useCallback(async () => {
    if (deleteConfirmViewId == null) {
      return;
    }
    await deleteWaterfallView(deleteConfirmViewId);
    if (selectedViewId === deleteConfirmViewId) {
      setSelectedViewId(null);
      clearView();
    }
    setDeleteConfirmViewId(null);
  }, [clearView, deleteConfirmViewId, deleteWaterfallView, selectedViewId]);

  const handleCancelDeleteView = useCallback(() => setDeleteConfirmViewId(null), []);

  const deleteConfirmViewName = useMemo(
    () => savedViews.find(v => v.id === deleteConfirmViewId)?.name,
    [savedViews, deleteConfirmViewId]
  );

  const {
    items: viewNavItems,
    activeViewId: requestedViewId,
    hasRequestedView,
    matchedViewId: urlMatchedViewId,
  } = useWaterfallViewNavItems({
    views: savedViews,
    urlState: urlStateWithData,
    isFirmPage: isFirmPage ?? false,
    firmSlug,
    companySlug,
    companyList: waterfallCompanyList,
    onSelectDefault: handleSelectCurrentView,
    onSelectView: setSelectedViewId,
  });
  const savedViewContextKey =
    savedViewsCompanyId != null && savedViewsMeasurementDateId != null
      ? `${savedViewsCompanyId}:${savedViewsMeasurementDateId}`
      : null;

  // Reset when company or measurement date changes so stale view IDs don't carry over.
  useEffect(() => {
    setSelectedViewId(null);
  }, [savedViewsCompanyId, savedViewsMeasurementDateId]);

  // Wait for the authoritative list before treating a missing selected view as deleted.
  useEffect(() => {
    if (
      savedViewsData != null &&
      !isSavedViewsFetching &&
      selectedViewId != null &&
      !savedViews.some(v => v.id === selectedViewId)
    ) {
      setSelectedViewId(null);
    }
  }, [isSavedViewsFetching, savedViews, savedViewsData, selectedViewId]);

  // Reject deleted, inaccessible, or cross-company View IDs without disturbing the remaining URL state.
  useEffect(() => {
    if (
      savedViewContextKey == null ||
      savedViewsData == null ||
      isSavedViewsFetching ||
      !hasRequestedView ||
      requestedViewId != null
    ) {
      return;
    }
    setSelectedViewId(null);
    clearView();
  }, [clearView, hasRequestedView, isSavedViewsFetching, requestedViewId, savedViewContextKey, savedViewsData]);

  // A View ID is authoritative on first load. Replace stale snapshot params with the latest saved configuration.
  useEffect(() => {
    if (savedViewContextKey == null || requestedViewId == null || isSavedViewsFetching) {
      return;
    }

    const hydrationKey = `${savedViewContextKey}:${requestedViewId}`;
    if (hydratedSavedViewKeyRef.current === hydrationKey) {
      if (isHydratingSavedViewRef.current && urlMatchedViewId === requestedViewId) {
        isHydratingSavedViewRef.current = false;
      }
      return;
    }

    hydratedSavedViewKeyRef.current = hydrationKey;
    setSelectedViewId(requestedViewId);

    if (urlMatchedViewId !== requestedViewId) {
      const requestedViewItem = viewNavItems.find(item => item.value === String(requestedViewId));
      if (requestedViewItem?.to) {
        isHydratingSavedViewRef.current = true;
        navigate(requestedViewItem.to, { replace: true });
      }
    } else {
      isHydratingSavedViewRef.current = false;
    }
  }, [isSavedViewsFetching, navigate, requestedViewId, savedViewContextKey, urlMatchedViewId, viewNavItems]);

  // Creation starts from Current, so add the new identity to the URL once the invalidated list contains it.
  useEffect(() => {
    if (selectedViewId == null || requestedViewId === selectedViewId || isSavedViewsFetching) {
      return;
    }
    const selectedViewItem = viewNavItems.find(item => item.value === String(selectedViewId));
    if (selectedViewItem?.to) {
      navigate(selectedViewItem.to, { replace: true });
    }
  }, [isSavedViewsFetching, navigate, requestedViewId, selectedViewId, viewNavItems]);

  // Default a newly entered company/date context to its first View without replacing explicit shared URL state.
  useEffect(() => {
    if (
      savedViewContextKey == null ||
      isSavedViewsFetching ||
      handledSavedViewContextRef.current === savedViewContextKey
    ) {
      return;
    }

    const isInitialContext = handledSavedViewContextRef.current == null;
    handledSavedViewContextRef.current = savedViewContextKey;

    if (
      savedViews.length === 0 ||
      requestedViewId != null ||
      (isInitialContext && hasExplicitInitialViewStateRef.current)
    ) {
      return;
    }

    const firstSavedViewItem = viewNavItems.find(item => item.value !== DEFAULT_VIEW_VALUE);
    if (firstSavedViewItem?.to) {
      const firstSavedViewId = Number(firstSavedViewItem.value);
      if (Number.isFinite(firstSavedViewId)) {
        setSelectedViewId(firstSavedViewId);
      }
      navigate(firstSavedViewItem.to, { replace: true });
    }
  }, [isSavedViewsFetching, navigate, requestedViewId, savedViewContextKey, savedViews.length, viewNavItems]);

  // Auto-save: when the URL diverges from the selected saved view, debounce-PATCH it.
  // Uses individual primitive deps (not the full urlStateWithData object) so the effect
  // only fires when a relevant value actually changes.
  const autoSaveMeasurementDate = urlStateWithData.selectedMeasurementDate?.cmd_id ?? null;
  const autoSaveCaptable = urlStateWithData.captableIdFromUrl ?? null;
  const autoSaveExitDate = urlStateWithData.exitDateFromUrl ?? null;
  const autoSaveEquityValue = urlStateWithData.equityValueFromUrl ?? null;
  const autoSaveEnterpriseValue = urlStateWithData.enterpriseValueFromUrl ?? null;
  const autoSavePlusCash = urlStateWithData.cashOverrideFromUrl ?? null;
  const autoSaveLessDebt = urlStateWithData.debtOverrideFromUrl ?? null;
  const autoSaveRelatedEntityAdjustments =
    serializeRelatedEntityAdjustments(urlStateWithData.relatedEntityAdjustmentsFromUrl) ?? '{}';
  const autoSaveForeignCurrency = String(urlStateWithData.foreignCurrencyFromUrl);
  const autoSaveCurrencyFormat = WATERFALL_UNITS_TO_CURRENCY_FORMAT[urlStateWithData.currencyUnitsFromUrl];
  const autoSaveForeignExchangeRate = urlStateWithData.foreignExchangeRateFromUrl ?? null;
  const isAutoSaveExchangeRateReady =
    Number.isFinite(autoSaveForeignExchangeRate) &&
    autoSaveForeignExchangeRate != null &&
    autoSaveForeignExchangeRate > 0;
  const autoSaveForeignExchangeRateIsOverridden = urlStateWithData.isForeignExchangeRateOverriddenFromUrl;
  const autoSaveForeignExchangeRateInputCurrency = urlStateWithData.foreignExchangeRateInputCurrencyFromUrl;

  // Avoid persisting the temporary null FX state produced by currency, date, or Cap Table transitions.
  useEffect(() => {
    if (
      selectedViewId == null ||
      !savedViews.some(view => view.id === selectedViewId) ||
      urlMatchedViewId === selectedViewId ||
      isHydratingSavedViewRef.current ||
      !isAutoSaveExchangeRateReady
    ) {
      return undefined;
    }

    const timerId = setTimeout(() => {
      updateWaterfallView({
        id: selectedViewId,
        payload: {
          measurement_date: autoSaveMeasurementDate,
          cap_table: autoSaveCaptable,
          exit_date: autoSaveExitDate ? moment(autoSaveExitDate, 'MM/DD/YYYY').format('YYYY-MM-DD') : null,
          exit_equity_value: autoSaveEquityValue != null ? String(autoSaveEquityValue) : null,
          exit_enterprise_value: autoSaveEnterpriseValue != null ? String(autoSaveEnterpriseValue) : null,
          plus_cash: autoSavePlusCash != null ? String(autoSavePlusCash) : null,
          less_debt: autoSaveLessDebt != null ? String(autoSaveLessDebt) : null,
          related_entity_adjustments: JSON.parse(autoSaveRelatedEntityAdjustments) as WaterfallView['related_entity_adjustments'],
          foreign_currency: autoSaveForeignCurrency,
          currency_format: autoSaveCurrencyFormat as WaterfallView['currency_format'],
          foreign_exchange_rate: autoSaveForeignExchangeRate != null ? String(autoSaveForeignExchangeRate) : null,
          foreign_exchange_rate_is_overridden: autoSaveForeignExchangeRateIsOverridden,
          foreign_exchange_rate_input_currency:
            autoSaveForeignExchangeRateInputCurrency as WaterfallView['foreign_exchange_rate_input_currency'],
        },
      });
    }, 400);

    return () => clearTimeout(timerId);
  }, [
    selectedViewId,
    savedViews,
    urlMatchedViewId,
    autoSaveMeasurementDate,
    autoSaveCaptable,
    autoSaveExitDate,
    autoSaveEquityValue,
    autoSaveEnterpriseValue,
    autoSavePlusCash,
    autoSaveLessDebt,
    autoSaveRelatedEntityAdjustments,
    autoSaveForeignCurrency,
    autoSaveCurrencyFormat,
    autoSaveForeignExchangeRate,
    isAutoSaveExchangeRateReady,
    autoSaveForeignExchangeRateIsOverridden,
    autoSaveForeignExchangeRateInputCurrency,
    updateWaterfallView,
  ]);

  const activeSavedViewId = selectedViewId ?? requestedViewId;
  const viewNavActiveValue = activeSavedViewId != null ? String(activeSavedViewId) : DEFAULT_VIEW_VALUE;

  const tertiaryNavLeftActions = useMemo<TertiaryNavigationLeftActionControlConfig[]>(
    () => [
      {
        key: 'add-view',
        type: 'addButton',
        testId: 'waterfall-add-view',
        disabled: savedViewsCompanyId == null || savedViewsMeasurementDateId == null || !isExchangeRateReady,
        onClick: handleOpenCreateViewDialog,
      },
    ],
    [handleOpenCreateViewDialog, isExchangeRateReady, savedViewsCompanyId, savedViewsMeasurementDateId]
  );

  const tertiaryNavActions = useMemo<TertiaryNavigationActionControlConfig[]>(
    () => [
      {
        type: 'currencySelector',
        currency: currencyDisplayLabel,
        currencyTooltipTitle,
        unit: unitDisplayLabel,
        onCurrencyClick: handleCurrencyMenuOpen,
        onUnitClick: handleUnitClick,
      },
    ],
    [currencyDisplayLabel, currencyTooltipTitle, handleCurrencyMenuOpen, handleUnitClick, unitDisplayLabel]
  );

  const tertiaryNavigation = useMemo(
    () => ({
      actions: tertiaryNavActions,
      items: viewNavItems,
      leftActions: tertiaryNavLeftActions,
      value: viewNavActiveValue,
      onItemMenuClick: handleItemMenuClick,
    }),
    [handleItemMenuClick, tertiaryNavActions, tertiaryNavLeftActions, viewNavActiveValue, viewNavItems]
  );

  useTertiaryNavigationRegistration(tertiaryNavigation);

  // ============================================================
  // Page Actions
  // ============================================================
  const hasPendingSaveChanges =
    !isEmpty(filesToSave) ||
    (!isEmpty(notes) && notesHasChanged) ||
    (!isEmpty(spreadsheetNotes) && spreadsheetNoteHasChanged);

  const saveNavContribution = useMemo(
    () => ({
      actions: [
        {
          type: 'button' as const,
          key: 'waterfall-save',
          label: 'Save Notes & Documents',
          variant: 'positive' as const,
          disabled: !hasPendingSaveChanges,
          onClick: saveDrawer,
        },
      ],
    }),
    [hasPendingSaveChanges, saveDrawer]
  );

  useSecondaryNavigationContributionRegistration(saveNavContribution);

  // ============================================================
  // Children Companies
  // ============================================================
  const childrenCompanies = useMemo(() => {
    if (Number(companyInfo?.id) === Number(selectedCompanyId) && childCompaniesFromRoute) {
      return childCompaniesFromRoute;
    }
    return (companyList ?? []).find(c => c.company_id === selectedCompanyId)?.child_companies ?? null;
  }, [childCompaniesFromRoute, companyInfo?.id, companyList, selectedCompanyId]);

  // ============================================================
  // Valid Securities (needed for distribution hook)
  // ============================================================
  const validSecurities = useMemo<SecurityForWaterfall[]>(() => {
    if (captableDetail) {
      return getValidSecurities(captableDetail).flatMap(security =>
        security.id == null ? [] : [{ id: security.id, name: security.name }]
      );
    }
    return [];
  }, [captableDetail]);

  // ============================================================
  // Waterfall Distribution
  // ============================================================
  const {
    isLoading: isDistributionLoading,
    scenarios,
    setScenarios,
    updatedWeightedShareValues,
  } = useWaterfallDistribution({
    selectedCaptable,
    equityValue: calculatedEquityValue,
    exitDate,
    captableDetail,
    fundOwnershipCapTableDetail: fundOwnershipCapTableDetail ?? null,
    capTableList,
    isInitializedFromUrl: isInitialized,
    isInitializingFromUrl: !isInitialized,
    selectedCompanyId,
    fundList,
    childrenCompanies,
    childrenCapTables,
    validSecurities,
  });

  // ============================================================
  // Portfolio/Related Entity Logic
  // ============================================================
  const isSelectedPortfolioPrimaryCapTable = useMemo(() => {
    if (!selectedCaptable || isEmpty(capTableList)) {
      return false;
    }
    return Boolean(capTableList.some(captable => captable.id === selectedCaptable && captable.is_primary));
  }, [selectedCaptable, capTableList]);

  const shouldRunRelatedEntitiesWaterfall = useMemo(
    () => isSelectedPortfolioPrimaryCapTable && (childrenCompanies ?? []).length > 0,
    [isSelectedPortfolioPrimaryCapTable, childrenCompanies]
  );

  const selectedCapTableVersion = useMemo(
    () => [...capTableList, ...childrenCapTables].find(ct => ct.id === selectedCaptable),
    [capTableList, childrenCapTables, selectedCaptable]
  );
  const selectedCompanyInformation = useMemo(() => {
    const capTableCompanyId = selectedCapTableVersion?.company_id ?? selectedCompanyId;
    return (
      (companyList ?? []).find(company => Number(company.company_id) === Number(capTableCompanyId)) ??
      (childrenCompanies ?? []).find(company => Number(company.company_id) === Number(capTableCompanyId))
    );
  }, [childrenCompanies, companyList, selectedCapTableVersion?.company_id, selectedCompanyId]);
  const selectedCompanyCapTableDestination = useMemo(() => {
    if (!isFirmPage || !firmSlug || !selectedCompanyInformation?.company_slug) {
      return null;
    }

    const queryParams = new URLSearchParams();
    if (companySelectedMeasurementDate?.date) {
      queryParams.set('date', companySelectedMeasurementDate.date);
    }
    if (selectedCapTableVersion?.slug) {
      queryParams.set('version', selectedCapTableVersion.slug);
    }

    const queryString = queryParams.toString();
    const baseUrl = captableUrl(firmSlug, selectedCompanyInformation.company_slug);
    return {
      companyName: selectedCompanyInformation.company_name || 'Company',
      url: queryString ? `${baseUrl}?${queryString}` : baseUrl,
    };
  }, [companySelectedMeasurementDate?.date, firmSlug, isFirmPage, selectedCapTableVersion, selectedCompanyInformation]);
  const capTableLinkLabelRef = useRef<HTMLSpanElement>(null);
  const selectedCompanySlug = selectedCompanyInformation?.company_slug ?? (!isFirmPage ? companyInfo?.slug : undefined);
  const incompleteFundOwnershipUrl = useMemo(() => {
    if (!firmInfo?.slug || !selectedCompanySlug) {
      return null;
    }

    return fundOwnershipUrl(firmInfo.slug, selectedCompanySlug);
  }, [firmInfo?.slug, selectedCompanySlug]);

  // ============================================================
  // Sync cap table URL when cap table list changes
  // If the current URL cap table is not in the new list, default to primary
  // ============================================================
  useEffect(() => {
    // Skip if cap table list is still loading or empty
    if (waterfallData.isCapTableListLoading || isChildrenCapTablesLoading || !capTableList.length) {
      return;
    }

    const currentCaptableId = urlStateWithData.captableIdFromUrl;

    // If URL has a cap table, check if it's in the current list (including children)
    if (currentCaptableId !== undefined) {
      const isInMainList = capTableList.some(ct => ct.id === currentCaptableId);
      const isInChildrenList = childrenCapTables.some(ct => ct.id === currentCaptableId);
      if (isInMainList || isInChildrenList) {
        return;
      } // Cap table is valid, nothing to do
    }

    // URL cap table is not in the list (or URL has no cap table) - set to primary
    const primaryCaptable = capTableList.find(ct => ct.is_primary);
    if (primaryCaptable) {
      urlStateWithData.setCaptable(primaryCaptable);
    }
  }, [
    capTableList,
    childrenCapTables,
    isChildrenCapTablesLoading,
    waterfallData.isCapTableListLoading,
    urlStateWithData.captableIdFromUrl,
    urlStateWithData.setCaptable,
    urlStateWithData,
  ]);

  // ============================================================
  // Company Info Fetch
  // ============================================================
  useEffect(() => {
    if (selectedCompanyId && (isEmpty(companyInfo) || selectedCompanyId !== companyInfo?.id)) {
      getCompanyInfoById(selectedCompanyId);
    }
  }, [selectedCompanyId, companyInfo, getCompanyInfoById]);

  // Keep the effective FO source (pro forma's own FO, else primary) for related-entity /
  // concluded share-value paths (Valuations pattern)
  useEffect(() => {
    mainFundOwnershipRef.current = fundOwnershipCapTableDetail?.fund_ownership || null;
  }, [fundOwnershipCapTableDetail]);

  // ============================================================
  // Firm Input Data
  // ============================================================
  const firmInputData = useMemo(() => {
    if (isFirmPage) {
      return {
        companyId: selectedCompanyId,
        measurementDateId: companySelectedMeasurementDate?.id,
      };
    }
    return undefined;
  }, [isFirmPage, selectedCompanyId, companySelectedMeasurementDate]);

  // ============================================================
  // Sheet Configurations
  // ============================================================
  const { sheetConfig: waterfallSheet } = useWaterfallSheetConfig({
    scenarioObject: scenarios,
    validSecurities,
    companyName: selectedCompanyInformation?.company_name ?? '',
    relatedEntityId: null,
  });

  const { spreadsheets: fundInvestmentSheets } = useFundInvestmentSheetConfig({
    weightedShareValues: updatedWeightedShareValues,
    validSecurities,
    idToAssociateTo: null,
  });

  const fundInvestmentSkeletonCount = useWaterfallSkeletonFundInvestmentSheetCount({
    fundInvestmentSheetCount: fundInvestmentSheets.length,
    hasSettledResults: updatedWeightedShareValues !== undefined,
  });

  const {
    ownersShareValuesMap,
    relatedEntitiesWaterfalls,
    isRelatedEntitiesWaterfallsLoading,
    childrenSheets,
    registerChildrenSheets,
    resetChildrenSheets,
    relatedEntitiesEnterpriseValues,
    relatedEntitiesEquityValues,
  } = useRelatedEntitiesWaterfalls({
    updatedWeightedShareValues,
    childrenCapTables,
    childrenCompanies: childrenCompanies ?? [],
    exitDate: exitDate ?? '',
    fundList: fundList ?? [],
    mainFundOwnershipRef,
    relatedEntityAdjustments: urlStateWithData.relatedEntityAdjustmentsFromUrl,
  });

  const activeRelatedEntitySheets = useMemo(
    () => getActiveRelatedEntitySheets(childrenSheets, shouldRunRelatedEntitiesWaterfall),
    [childrenSheets, shouldRunRelatedEntitiesWaterfall]
  );

  const fundInvestmentSheetNames = useMemo(
    () =>
      [...fundInvestmentSheets, ...activeRelatedEntitySheets]
        .map(sheet => sheet?.name)
        .filter((name): name is string => Boolean(name))
        .slice()
        .sort(),
    [fundInvestmentSheets, activeRelatedEntitySheets]
  );

  // Fund summary exit-proceeds exprs reference FundInvestment_* sheets. Wait until weighted
  // share values have been computed so those sheets exist (avoids hang on missing symbols).
  // Do not pass fundIrrMap into this config — IRR is written via onChange only; feeding it
  // back into the sheet rebuilds the workbook and loops.
  const { sheetConfig: summarySheetConfig, hasFirmTotalLoanValueCell } = useWaterfallFundSummarySheetConfig({
    captable:
      updatedWeightedShareValues !== undefined && isExchangeRateReady && historicalInvestedCapitalTotals
        ? ownershipCaptable
        : null,
    fundList,
    childrenCompanies,
    calculatedEquityValue,
    fundInvestmentSheetNames,
    historicalInvestedCapitalTotals: historicalInvestedCapitalTotals ?? [],
  });

  const { spreadsheet: firmTotalSheet } = useFirmTotalSheetConfig({
    weightedShareValues: updatedWeightedShareValues,
    securityCount: validSecurities.length,
    investmentsInPortfolioSheets: fundInvestmentSheets,
    allInvestmentsSheets: [...fundInvestmentSheets, ...activeRelatedEntitySheets],
    isSelectedPortfolioPrimaryCapTable,
    fundInvestmentsSheetsNamesRef,
  });

  const inputPolicy = useMemo(
    () =>
      createWaterfallInputUnitPolicy({
        enabled: Boolean(firmInfo?.use_selected_number_format_for_inputs),
        units: urlStateWithData.currencyUnitsFromUrl,
      }),
    [firmInfo?.use_selected_number_format_for_inputs, urlStateWithData.currencyUnitsFromUrl]
  );

  const relatedEntityChildrenCompanies = useMemo(() => childrenCompanies ?? [], [childrenCompanies]);

  const relatedEntityInputSheets = useRelatedEntityInputSheets({
    childrenCapTables,
    childrenCompanies: relatedEntityChildrenCompanies,
    exchangeRate: waterfallFXProvider.exchangeRate,
    inputCurrencyCode: inputFormat.currency.code ? String(inputFormat.currency.code) : undefined,
    inputPolicy,
    isAdditionalCurrencySelected,
    outputCurrencyCode: format.currency.code ? String(format.currency.code) : undefined,
    relatedEntitiesEnterpriseValues,
    relatedEntityAdjustments: urlStateWithData.relatedEntityAdjustmentsFromUrl,
    shouldRunRelatedEntitiesWaterfall,
  });

  const inputSheet = useCreateInputSheet({
    exitDate,
    // Pass as string to match dropdown option values
    capTableId: selectedCaptable !== undefined ? String(selectedCaptable) : undefined,
    equityValue,
    enterpriseValue,
    firmOptions: firmInputData,
    childrenCapTables,
    parentCapTables: capTableList,
    fundInvestmentsSheetsNamesRef,
    plusCashIsFromFinancials: cashIsFromFinancials,
    lessDebtIsFromFinancials: debtIsFromFinancials,
    plusCash: effectiveCash,
    lessDebt: effectiveDebt,
    isAdditionalCurrencySelected,
    exchangeRate: waterfallFXProvider.exchangeRate,
    isExchangeRateOverridden: urlStateWithData.isForeignExchangeRateOverriddenFromUrl,
    exchangeRateInputCurrency: urlStateWithData.foreignExchangeRateInputCurrencyFromUrl,
    inputPolicy,
    hasFirmTotalLoanValueCell,
  });

  const sheets = useMemo(
    () =>
      [
        inputSheet,
        exchangeRateSheet,
        waterfallSheet,
        ...fundInvestmentSheets,
        ...relatedEntityInputSheets.map(entry => entry.inputSheet),
        ...activeRelatedEntitySheets,
        firmTotalSheet,
        summarySheetConfig,
      ].filter(sheet => !!sheet),
    [
      activeRelatedEntitySheets,
      exchangeRateSheet,
      firmTotalSheet,
      fundInvestmentSheets,
      inputSheet,
      relatedEntityInputSheets,
      summarySheetConfig,
      waterfallSheet,
    ]
  );

  const {
    cells: workbookCells,
    data: workbookData,
    evaluatedSpreadsheets,
    onChange,
    workbookContextValue,
  } = useWorkbook(sheets);

  // New configs have no grid until useWorkbook commits them. Keep evaluated
  // Input/Summary/Firm Total sheets mounted so child edits cannot resize the section above them.
  const displayedInputSheet = inputSheet?.name ? workbookData?.[inputSheet.name] : undefined;
  const displayedSummarySheet = summarySheetConfig?.name ? workbookData?.[summarySheetConfig.name] : undefined;
  const displayedFirmTotalSheet = firmTotalSheet?.name ? workbookData?.[firmTotalSheet.name] : undefined;
  const isSummaryRecalculating = isRelatedEntitiesWaterfallsLoading || displayedSummarySheet !== summarySheetConfig;

  // Synchronize URL-backed overrides and validation without rebuilding the stable input sheets.
  useEffect(() => {
    syncRelatedEntityInputAdjustments({
      onChange,
      relatedEntityAdjustments: urlStateWithData.relatedEntityAdjustmentsFromUrl,
      workbookCells,
    });
    syncRelatedEntityInputValidation({
      relatedEntitiesEquityValues,
      workbookCells,
    });
  }, [
    autoSaveRelatedEntityAdjustments,
    onChange,
    relatedEntitiesEquityValues,
    workbookCells,
    urlStateWithData.relatedEntityAdjustmentsFromUrl,
  ]);

  useExchangeRateSheet({ cells: workbookCells, onChange, exchangeRate: waterfallFXProvider.exchangeRate });

  // ============================================================
  // Sync URL state to spreadsheet cells
  // When URL changes (e.g., via defaults), update the corresponding cells
  // ============================================================
  useEffect(() => {
    if (!workbookCells?.InputSheet) {
      return;
    }

    // Sync cap table value from URL to cell (pass as string to match dropdown option values)
    const capTableCell = Object.values(workbookCells.InputSheet).find(c => c.alias === CAP_TABLE_ALIAS);
    if (capTableCell && selectedCaptable !== undefined) {
      const currentValue = capTableCell.value;
      const selectedCaptableStr = String(selectedCaptable);
      // Only update if the value is different (avoid infinite loops)
      if (String(currentValue) !== selectedCaptableStr) {
        onChange(capTableCell, selectedCaptableStr);
      }
    }

    // Sync measurement date value from URL to cell (for firm pages, pass as string)
    if (isFirmPage) {
      const measurementDateCell = Object.values(workbookCells.InputSheet).find(c => c.alias === MEASUREMENT_DATE_ALIAS);
      const resolvedMeasurementDateId = urlStateWithData.selectedMeasurementDate?.id;
      if (measurementDateCell && resolvedMeasurementDateId !== undefined) {
        const currentValue = measurementDateCell.value;
        const measurementDateIdStr = String(resolvedMeasurementDateId);
        if (String(currentValue) !== measurementDateIdStr) {
          onChange(measurementDateCell, measurementDateIdStr);
        }
      }
    }
  }, [workbookCells, selectedCaptable, urlStateWithData.selectedMeasurementDate?.id, isFirmPage, onChange]);

  // ============================================================
  // IRR Data
  // ============================================================
  const { isInputsLoaded } = useWaterfallIrr({
    workbookCells,
    irrCaptableId: fundOwnershipCapTableDetail?.id,
    exitDate,
    onChange,
    isFirmPage: isFirmPage ?? false,
    companyList,
    isFinancialsLoading: waterfallData.isFinancialsLoading,
  });

  // ============================================================
  // Input Handlers
  // ============================================================
  const { onWorkbookChange } = useWaterfallInputHandlers({
    urlState: urlStateWithData,
    onChange,
    setScenarios,
    resetChildrenSheets,
    capTableList,
    childrenCapTables,
    companyMeasurementDates,
    exchangeRate: waterfallFXProvider.exchangeRate,
    inputPolicy,
  });

  // ============================================================
  // Loading State
  // ============================================================
  useEffect(() => {
    if (isFirmPage) {
      setIsLoading(!companyList?.length);
    }
  }, [companyList, isFirmPage]);

  const combinedIsLoading =
    isLoading ||
    waterfallData.isLoading ||
    isInitializing ||
    isInvestedCapitalLoading ||
    (isCurrencyLoading && !isExchangeRateRetrying);
  const canRenderWaterfallResults = Boolean(
    waterfallSheet && workbookData && updatedWeightedShareValues && isExchangeRateReady
  );
  const [isWaterfallResultsPresentationReady, setIsWaterfallResultsPresentationReady] = useState(false);

  // Give mounted spreadsheets two frames to finish measuring before replacing the matching skeleton.
  useEffect(() => {
    if (combinedIsLoading || isDistributionLoading || !canRenderWaterfallResults) {
      setIsWaterfallResultsPresentationReady(false);
      return undefined;
    }

    let secondAnimationFrame: number | undefined;
    const firstAnimationFrame = window.requestAnimationFrame(() => {
      secondAnimationFrame = window.requestAnimationFrame(() => {
        setIsWaterfallResultsPresentationReady(true);
      });
    });

    return () => {
      window.cancelAnimationFrame(firstAnimationFrame);
      if (secondAnimationFrame !== undefined) {
        window.cancelAnimationFrame(secondAnimationFrame);
      }
    };
  }, [canRenderWaterfallResults, combinedIsLoading, isDistributionLoading]);

  const isWaterfallResultsTransitioning =
    isDistributionLoading || (canRenderWaterfallResults && !isWaterfallResultsPresentationReady);
  const isSelectedCapTableEmpty = Boolean(
    selectedCaptable && capTableList.some(capTable => capTable.id === selectedCaptable && capTable.is_empty)
  );
  // Mirror the distribution preconditions so non-runnable inputs release the full skeleton instead of hanging.
  const shouldExpectWaterfallResults = Boolean(
    captableDetail &&
      calculatedEquityValue &&
      exitDate &&
      moment(exitDate, ['MM/DD/YYYY', 'YYYY-MM-DD'], true).isValid() &&
      selectedCaptable &&
      isInitialized &&
      !isSelectedCapTableEmpty
  );
  const [isHoldingFullWaterfallSkeleton, setIsHoldingFullWaterfallSkeleton] = useState(Boolean(selectedCompanyId));
  const hasStartedHeldDistributionRef = useRef(false);

  // Bridge data initialization into the first distribution so reloads use one stable skeleton-to-table handoff.
  useEffect(() => {
    if (
      shouldReleaseFullWaterfallSkeleton({
        combinedIsLoading,
        hasUnavailableCompanyFromUrl: urlStateWithData.hasUnavailableCompanyFromUrl,
        isInitializationUnavailable,
        selectedCompanyId,
      })
    ) {
      hasStartedHeldDistributionRef.current = false;
      setIsHoldingFullWaterfallSkeleton(false);
      return;
    }

    if (combinedIsLoading) {
      if (isDistributionLoading) {
        hasStartedHeldDistributionRef.current = true;
      }
      setIsHoldingFullWaterfallSkeleton(true);
      return;
    }

    if (!isHoldingFullWaterfallSkeleton) {
      return;
    }

    if (!isInitialized) {
      return;
    }

    if (!shouldExpectWaterfallResults) {
      hasStartedHeldDistributionRef.current = false;
      setIsHoldingFullWaterfallSkeleton(false);
      return;
    }

    if (isDistributionLoading) {
      hasStartedHeldDistributionRef.current = true;
      return;
    }

    if (isWaterfallResultsPresentationReady || (hasStartedHeldDistributionRef.current && !canRenderWaterfallResults)) {
      hasStartedHeldDistributionRef.current = false;
      setIsHoldingFullWaterfallSkeleton(false);
    }
  }, [
    canRenderWaterfallResults,
    combinedIsLoading,
    isDistributionLoading,
    isHoldingFullWaterfallSkeleton,
    isInitializationUnavailable,
    isInitialized,
    isWaterfallResultsPresentationReady,
    selectedCompanyId,
    shouldExpectWaterfallResults,
    urlStateWithData.hasUnavailableCompanyFromUrl,
  ]);

  const isFullWaterfallSkeletonVisible =
    !urlStateWithData.hasUnavailableCompanyFromUrl && (combinedIsLoading || isHoldingFullWaterfallSkeleton);
  const shouldShowUnavailableCompanyNotice =
    urlStateWithData.hasUnavailableCompanyFromUrl && !isFullWaterfallSkeletonVisible;
  const shouldShowExchangeRateError =
    !isFullWaterfallSkeletonVisible && (hasExchangeRateLoadError || isExchangeRateRetrying) && !isExchangeRateReady;
  const shouldShowInvestedCapitalError =
    !isFullWaterfallSkeletonVisible &&
    hasInvestedCapitalLoadError &&
    !isInvestedCapitalLoading &&
    historicalInvestedCapitalTotals == null;

  // ============================================================
  // Context Values
  // ============================================================
  const waterfallContextValues = useMemo(
    () => ({
      companyList: waterfallCompanyList ?? [],
      measurementDates: companyMeasurementDates,
      capTableList,
      workbookData,
      onChange,
      format,
      formatDispatch,
    }),
    [waterfallCompanyList, companyMeasurementDates, capTableList, workbookData, onChange, format, formatDispatch]
  );

  // ============================================================
  // Render
  // ============================================================
  return (
    <>
      {viewDialogState != null && (
        <SaveWaterfallViewDialog
          key={viewDialogState.mode === 'edit' ? `edit-${viewDialogState.viewId}` : 'create'}
          buttonLabel={viewDialogState.mode === 'edit' ? 'Save' : 'Create'}
          initialName={viewDialogState.mode === 'edit' ? viewDialogState.initialName : ''}
          open
          isSaving={viewDialogState.mode === 'edit' ? isUpdatingView : isCreatingView}
          onSave={handleSubmitView}
          onClose={handleCloseViewDialog}
          title={viewDialogState.mode === 'edit' ? 'Edit Waterfall View' : 'Create Waterfall View'}
        />
      )}
      <CurrencyMenu
        data-testid="waterfall-currency-menu"
        anchorEl={currencyMenuAnchorEl}
        currencies={currencies}
        isCurrencyLoading={isCurrencyLoading}
        onClose={handleCurrencyMenuClose}
        onCurrencyChange={handleCurrencyChange}
        showMore={showMore}
        showLess={showLess}
        usedCurrencies={usedCurrencies}
      />
      <Menu anchorEl={viewMenuState?.anchorEl} open={viewMenuState != null} onClose={handleCloseViewMenu}>
        <MenuItem data-testid="waterfall-edit-view" onClick={handleRequestEditView}>
          Edit View
        </MenuItem>
        <MenuItem data-testid="waterfall-delete-view" onClick={handleRequestDeleteView}>
          Delete
        </MenuItem>
      </Menu>
      <ConfirmationDialog
        open={deleteConfirmViewId != null}
        title="Delete Saved View"
        isLoading={isDeletingView}
        onClose={handleCancelDeleteView}
        actions={[
          { label: 'Cancel', variant: 'outlined' },
          {
            label: 'Delete',
            type: 'danger',
            variant: 'contained',
            callback: () => {
              handleConfirmDeleteView();
            },
          },
        ]}>
        <ConfirmationDelete
          customMessage={`Are you sure you want to delete "${deleteConfirmViewName ?? 'this view'}"?`}
        />
      </ConfirmationDialog>
      <WorkbookContext.Provider value={workbookContextValue}>
        <WaterfallFXContext.Provider value={waterfallFXProvider}>
          <WaterfallContext.Provider value={waterfallContextValues}>
            <CustomContainer className={WATERFALL_CONTAINER_CLASS} sx={{ position: 'relative', pt: 1 }}>
              {isFullWaterfallSkeletonVisible && (
                <WaterfallLoadingSkeleton
                  fundInvestmentSheetCount={fundInvestmentSkeletonCount}
                  isAdditionalCurrencySelected={isAdditionalCurrencySelected}
                  isFirmPage={isFirmPage}
                  showFirmTotalSheet={Boolean(firmTotalSheet)}
                  summaryColumnCount={summarySheetConfig?.columns.length}
                />
              )}

              <CustomContainer
                aria-hidden={isFullWaterfallSkeletonVisible || undefined}
                sx={isFullWaterfallSkeletonVisible ? HIDDEN_WATERFALL_PRESENTATION_STYLES : { position: 'relative' }}>
                {displayedInputSheet?.data && isInputsLoaded && (
                  <>
                    <Grid container alignItems="flex-start" columnSpacing={2} rowSpacing={1} size={{ xs: 12 }}>
                      <Grid
                        className={WATERFALL_INPUT_CONTAINER_CLASS}
                        id="waterfall-inputs"
                        size={isFirmPage ? { sm: 'auto', xs: 12 } : { xs: 12 }}>
                        {shouldShowUnavailableCompanyNotice ? (
                          <Alert
                            data-testid="waterfall-unavailable-company"
                            severity="warning"
                            sx={UNAVAILABLE_COMPANY_NOTICE_STYLES}
                            visualVariant="inline">
                            Only Portfolio Companies can be used in Waterfall.
                          </Alert>
                        ) : null}
                        <WaterfallTableFormatter />
                        <ScalarSpreadsheet
                          {...displayedInputSheet}
                          data={displayedInputSheet.data ?? []}
                          totalData={displayedInputSheet.totalData ?? []}
                          titleData={displayedInputSheet.titleData ?? []}
                          format={inputFormat}
                          selectionSummaryUsesCellCurrencyFormat
                          showToolbar
                          formatDispatch={formatDispatch}
                          onChange={onWorkbookChange}
                          disableUnsavedChanges
                          disableVerticalScroll
                          sheet={displayedInputSheet as unknown as ScalarSpreadsheetProps['sheet']}
                        />
                      </Grid>

                      {selectedCompanyCapTableDestination ? (
                        <Grid display="flex" minWidth={0} size={{ sm: 'grow', xs: 12 }} sx={{ mt: { sm: 6 } }}>
                          <OverflowTooltip
                            measureRef={capTableLinkLabelRef}
                            placement="top-start"
                            title={`Go to ${selectedCompanyCapTableDestination.companyName}`}>
                            <TextLink<typeof RouterLink>
                              component={RouterLink}
                              data-testid="firm-waterfall-cap-table-link"
                              to={selectedCompanyCapTableDestination.url}
                              visualVariant="compact"
                              sx={{
                                display: 'inline-flex',
                                lineHeight: 1.3,
                                maxWidth: '100%',
                                minWidth: 0,
                              }}>
                              <Box
                                component="span"
                                ref={capTableLinkLabelRef}
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}>
                                Go to {selectedCompanyCapTableDestination.companyName}
                              </Box>
                            </TextLink>
                          </OverflowTooltip>
                        </Grid>
                      ) : null}
                    </Grid>

                    {displayedSummarySheet && isExchangeRateReady && (
                      <Grid
                        aria-busy={isSummaryRecalculating || undefined}
                        aria-hidden={isWaterfallResultsTransitioning || undefined}
                        className={WATERFALL_SUMMARY_CONTAINER_CLASS}
                        size={{ xs: 12 }}
                        sx={isWaterfallResultsTransitioning ? HIDDEN_WATERFALL_PRESENTATION_STYLES : undefined}>
                        <WaterfallRecalculation isRecalculating={isSummaryRecalculating}>
                          <WaterfallTableFormatter />
                          <ScalarSpreadsheet
                            {...displayedSummarySheet}
                            data={displayedSummarySheet.data ?? []}
                            totalData={displayedSummarySheet.totalData ?? []}
                            titleData={displayedSummarySheet.titleData ?? []}
                            format={format}
                            selectionSummaryUsesCellCurrencyFormat
                            showToolbar
                            formatDispatch={formatDispatch}
                            onChange={onWorkbookChange}
                            disableUnsavedChanges
                            disableVerticalScroll
                            sheet={displayedSummarySheet as unknown as ScalarSpreadsheetProps['sheet']}
                          />
                        </WaterfallRecalculation>
                      </Grid>
                    )}
                  </>
                )}
              </CustomContainer>

              {shouldShowExchangeRateError ? (
                <Alert
                  action={
                    <Button
                      data-testid="waterfall-exchange-rate-retry"
                      loading={isExchangeRateRetrying}
                      loadingBehavior="inline"
                      onClick={() => refetchExchangeRate()}
                      sizeVariant="compact"
                      startIcon={<RefreshRounded fontSize="inherit" />}
                      sx={{
                        '&[data-loading="true"].Mui-disabled': {
                          backgroundColor: theme => alpha(theme.palette.primary.main, 0.08),
                          borderColor: 'primary.main',
                          color: 'primary.main',
                          opacity: 1,
                        },
                      }}
                      visualVariant="primaryOutline">
                      {isExchangeRateRetrying ? 'Retrying...' : 'Retry'}
                    </Button>
                  }
                  data-testid="waterfall-exchange-rate-error"
                  severity="error"
                  sx={{ mt: 2 }}
                  visualVariant="inline">
                  Converted results are unavailable because the foreign exchange rate for the selected Exit Date and
                  currency could not be loaded.
                </Alert>
              ) : null}

              {shouldShowInvestedCapitalError ? (
                <Alert
                  action={
                    <Button
                      data-testid="waterfall-invested-capital-retry"
                      loading={isInvestedCapitalFetching}
                      loadingBehavior="inline"
                      onClick={() => refetchInvestedCapital()}
                      sizeVariant="compact"
                      startIcon={<RefreshRounded fontSize="inherit" />}
                      visualVariant="primaryOutline">
                      {isInvestedCapitalFetching ? 'Retrying...' : 'Retry'}
                    </Button>
                  }
                  data-testid="waterfall-invested-capital-error"
                  severity="error"
                  sx={{ mt: 2 }}
                  visualVariant="inline">
                  Invested Capital could not be calculated for the selected cap table and currency.
                </Alert>
              ) : null}

              {!isFullWaterfallSkeletonVisible && isWaterfallResultsTransitioning && (
                <WaterfallLoadingSkeleton
                  fundInvestmentSheetCount={fundInvestmentSkeletonCount}
                  showFirmTotalSheet={Boolean(firmTotalSheet)}
                  showInput={false}
                  summaryColumnCount={summarySheetConfig?.columns.length}
                />
              )}

              {waterfallSheet && workbookData && updatedWeightedShareValues && isExchangeRateReady && (
                <CustomContainer
                  aria-hidden={isFullWaterfallSkeletonVisible || isWaterfallResultsTransitioning || undefined}
                  sx={
                    isFullWaterfallSkeletonVisible || isWaterfallResultsTransitioning
                      ? HIDDEN_WATERFALL_PRESENTATION_STYLES
                      : undefined
                  }>
                  <CustomCompactContainer>
                    <CustomContainer>
                      <Grid size={{ xs: 12 }}>
                        <WaterfallTables
                          waterfallSheet={waterfallSheet}
                          fundInvestmentsSheets={fundInvestmentSheets}
                          firmTotalSheet={
                            Object.entries(ownersShareValuesMap ?? {}).flat().length
                              ? undefined
                              : displayedFirmTotalSheet
                          }
                        />
                      </Grid>

                      {relatedEntityInputSheets.map(
                        ({ companyId: relatedEntityId, inputSheet: childInputSheet, relatedEntity }) => (
                          <Grid
                            className={WATERFALL_RELATED_ENTITY_CONTAINER_CLASS}
                            key={relatedEntityId}
                            size={{ xs: 12 }}>
                            <RelatedEntityWaterfallSheets
                              relatedEntity={relatedEntity}
                              scenarioResult={relatedEntitiesWaterfalls?.[relatedEntityId]}
                              childrenCapTables={childrenCapTables}
                              ownedSharesValue={ownersShareValuesMap?.[relatedEntityId]}
                              registerChildrenSheets={registerChildrenSheets}
                              inputFormat={inputFormat}
                              inputSheet={childInputSheet}
                              isLoading={isRelatedEntitiesWaterfallsLoading || evaluatedSpreadsheets !== sheets}
                              equityValue={relatedEntitiesEquityValues?.[relatedEntityId]}
                              onInputChange={onWorkbookChange}
                            />
                          </Grid>
                        )
                      )}
                    </CustomContainer>

                    {exitDate &&
                    (equityValue || calculatedEquityValue) &&
                    companySelectedMeasurementDate &&
                    selectedCompanyInformation &&
                    selectedCaptable ? (
                        <DownloadWaterfall
                          exitDate={exitDate}
                          equityValue={equityValue ?? (calculatedEquityValue as number)}
                          companyMeasurementDate={companySelectedMeasurementDate}
                          selectedCapTableId={selectedCaptable}
                          selectedCompanyId={Number(selectedCompanyInformation.company_id)}
                        />
                      ) : null}
                  </CustomCompactContainer>
                </CustomContainer>
              )}

              {validSecurities?.length === 0 && scenarios !== undefined && selectedCaptable && (
                <EmptyTableMessage
                  title="The selected company has an incomplete cap table"
                  tableTerms={{
                    tableName: 'Cap table',
                    pluralColumnName: 'Securities',
                    columnName: 'Security',
                  }}
                />
              )}

              {updatedWeightedShareValues && isEmpty(updatedWeightedShareValues) && (
                <NeedsFundOwnershipFirst
                  fundOwnershipURL={incompleteFundOwnershipUrl}
                  measurementDates={companyMeasurementDates}
                  selectedMeasurementDate={companySelectedMeasurementDate}
                  title={ALLOCATION_EMPTY_FUND_OWNERSHIP_TITLE}
                  tagline={ALLOCATION_EMPTY_FUND_OWNERSHIP_TAGLINE}
                />
              )}
              <MDDialogue />
            </CustomContainer>
          </WaterfallContext.Provider>
        </WaterfallFXContext.Provider>
      </WorkbookContext.Provider>
      {companySelectedMeasurementDate && selectedCaptable && (
        <WidgetDrawer
          applicationContext={WidgetDrawerApplicationContext.CAP_TABLE}
          notesProps={{
            pageType: SpreadsheetNote.page_type.WTRFL,
            pageTypeKey: WATERFALL_PAGE_KEY,
            pageTypeId: companySelectedMeasurementDate.cmd_id,
            notes,
            setNotes,
            onAddNote,
            onUpdateNotes,
            onDeleteNote,
            isDisabled: false,
          }}
          documentReferencesProps={{
            selectedMeasurementDate: companySelectedMeasurementDate,
            referenceType: CAP_TABLE_REFERENCE_TYPE,
            currentPage: CAP_TABLE_REFERENCE_TITLE,
            referencedFeatureId: selectedCaptable,
            filesToSave,
            setFilesToSave,
            isDisplayingRowNumbers: false,
            isDisabled: false,
          }}
          spreadsheetNotesProps={{
            pageTypeId: companySelectedMeasurementDate.cmd_id as number,
            pageType: SpreadsheetNote.page_type.WTRFL,
            pageTypeKey: WATERFALL_PAGE_KEY,
            notes: spreadsheetNotes,
            setNotes: setSpreadsheetNotes,
            saveNotes: () => {
              saveSpreadsheetNotes();
            },
            handleOnDeleteNote: handleOnDeleteSpreadsheetNote,
          }}
        />
      )}
    </>
  );
};

export default WaterfallDisplay;
